import { useEffect, useRef, useState } from "react";
import { numeroDocumentoDe } from "./tradicion";
import "./Bloqpadr.css";

/* Cuatro tarjetas por fila: el padre mas tres hijos. El ancho no se puede
   fijar en CSS porque depende del espacio libre de la fila (que cambia al abrir
   el panel del visor), asi que se calcula aqui y se publica como --bp-card.

   ESPACIO_FIJO son los pixeles de la fila que no son tarjeta: padding de
   .bp-row (14+14), sus dos gap de 8, el separador "HIJOS" (~40), las dos
   flechas de 32 con sus gap de 8 (80) y los gap de 10 entre hijos. */
const POR_FILA = 4;
const HIJOS_POR_FILA = POR_FILA - 1;
const GAP_TARJETA = 10;
const ESPACIO_FIJO = 28 + 16 + 40 + 80 + (HIJOS_POR_FILA - 1) * GAP_TARJETA;
const ANCHO_MIN = 200;
const ANCHO_MAX = 380;

/**
 * Ancho de tarjeta para que en la fila quepan POR_FILA tarjetas (padre + hijos)
 * @param {number} anchoFila
 * @returns {number}
 */
const anchoTarjeta = (anchoFila) => {
  const ancho = Math.floor((anchoFila - ESPACIO_FIJO) / POR_FILA);
  return Math.min(ANCHO_MAX, Math.max(ANCHO_MIN, ancho));
};

const Bloqpadr = ({ padre, hijos, renderCard, onAsociarHijo }) => {
  const viewportRef = useRef(null);
  const filaRef = useRef(null);
  const [ancho, setAncho] = useState(ANCHO_MIN);
  const total = hijos.length;
  // Numero de factura o de documento de transporte, junto al tipo documental
  const numeroDoc = numeroDocumentoDe(padre);
  const [plegado, setPlegado] = useState(false);
  const [nav, setNav] = useState({ desde: 1, hasta: 0, offIzq: true, offDer: true, overflow: false });

  const paso = ancho + GAP_TARJETA;

  const actualizar = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    const visibles = Math.max(1, Math.round(vp.clientWidth / paso));
    const primero = Math.round(vp.scrollLeft / paso);
    setNav({
      desde: total === 0 ? 0 : primero + 1,
      hasta: Math.min(primero + visibles, total),
      offIzq: vp.scrollLeft <= 1,
      offDer: vp.scrollLeft + vp.clientWidth >= vp.scrollWidth - 1,
      overflow: vp.scrollWidth > vp.clientWidth + 1,
    });
  };

  useEffect(() => {
    actualizar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, ancho]);

  // El ancho se recalcula cuando cambia el espacio de la fila (resize de la
  // ventana o apertura/cierre del panel del visor).
  useEffect(() => {
    const fila = filaRef.current;
    if (!fila) return;
    const medir = () => setAncho(anchoTarjeta(fila.clientWidth));
    medir();
    // ResizeObserver cubre los cambios que no mueven la ventana (abrir o cerrar
    // el panel del visor); el evento resize queda como respaldo porque hay
    // navegadores embebidos donde el observer no entrega notificaciones.
    window.addEventListener("resize", medir);
    const obs = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
    if (obs) obs.observe(fila);
    return () => {
      window.removeEventListener("resize", medir);
      if (obs) obs.disconnect();
    };
  }, [plegado]);

  const scrollPor = (dir) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const visibles = Math.max(1, Math.round(vp.clientWidth / paso));
    vp.scrollBy({ left: dir * visibles * paso, behavior: "smooth" });
  };

  return (
    <div className="bp-block">
      <div className="bp-hdr">
        {/* Toda la barra es el boton de plegar: flecha, tipo documental, numero
            e insignias van dentro y el boton crece para ocupar el espacio libre,
            asi el clic funciona en cualquier punto de la cabecera y no solo
            sobre las letras. Lo unico que queda afuera es "Asociar hijo", que
            tiene su propia accion. */}
        <button
          type="button"
          className="bp-titulo"
          onClick={() => setPlegado((v) => !v)}
          title={plegado ? "Desplegar seccion" : "Plegar seccion"}
          aria-expanded={!plegado}
        >
          <i className={`bi ${plegado ? "bi-chevron-right" : "bi-chevron-down"} bp-plegar`}></i>
          <span className="bp-hdr__tipo">{padre.TIPODESX}</span>
          {numeroDoc && <span className="bp-hdr__numero">{numeroDoc}</span>}
          {total > 0 ? (
            <span className="bp-hdr__count">
              {total} hijo{total > 1 ? "s" : ""}
            </span>
          ) : (
            <span className="bp-hdr__noh">Sin hijos</span>
          )}
          {nav.overflow && !plegado && (
            <span className="bp-hdr__nav">
              {nav.desde} – {nav.hasta} de {total}
            </span>
          )}
        </button>
        {onAsociarHijo && !plegado && (
          <button
            type="button"
            className="bp-asociar"
            onClick={() => onAsociarHijo(padre)}
            title="Asociar documento hijo"
          >
            + Asociar hijo
          </button>
        )}
      </div>

      {!plegado && (
      <div className="bp-row" ref={filaRef} style={{ "--bp-card": `${ancho}px` }}>
        <div className="bp-padre">{renderCard(padre)}</div>

        <div className="bp-sep">
          <span className="bp-sep__lbl">Hijos</span>
        </div>

        {total === 0 ? (
          <div className="bp-hijos">
            <div
              className={`bp-nohijo ${onAsociarHijo ? "bp-nohijo--click" : ""}`}
              onClick={onAsociarHijo ? () => onAsociarHijo(padre) : undefined}
            >
              {onAsociarHijo ? "Sin documentos hijos · Toca para asociar" : "Sin documentos hijos"}
            </div>
          </div>
        ) : (
          <div className="bp-hijos">
            {nav.overflow && (
              <button
                type="button"
                className={`bp-arrow ${nav.offIzq ? "bp-arrow--off" : ""}`}
                onClick={() => scrollPor(-1)}
                title="Anterior"
                disabled={nav.offIzq}
              >
                ‹
              </button>
            )}
            <div className="bp-viewport" ref={viewportRef} onScroll={actualizar}>
              <div className="bp-track">
                {hijos.map((hijo) => (
                  <div className="bp-hijo" key={hijo.IDARCHXX}>
                    {renderCard(hijo)}
                  </div>
                ))}
                {onAsociarHijo && (
                  <button
                    type="button"
                    className="bp-add-end"
                    onClick={() => onAsociarHijo(padre)}
                    title="Asociar documento hijo"
                  >
                    +
                  </button>
                )}
              </div>
            </div>
            {nav.overflow && (
              <button
                type="button"
                className={`bp-arrow ${nav.offDer ? "bp-arrow--off" : ""}`}
                onClick={() => scrollPor(1)}
                title="Siguiente"
                disabled={nav.offDer}
              >
                ›
              </button>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default Bloqpadr;
